// src/pages/Login.tsx - Modified version
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { login, clearError } from "../redux/slices/authSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  Shield,
  AlertCircle,
  User,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";

const Login: React.FC = () => {
  const [loginInput, setLoginInput] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isEmail, setIsEmail] = useState<boolean | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, user, token } = useSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    if (isAuthenticated && user) {
      if (!user.email_verified_at) {
        navigate("/verify-email", { state: { email: user.email } });
      } else {
        navigate("/dashboard");
      }
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (error) {
      // Display the specific error message from backend
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (token && user && !user.email_verified_at) {
      navigate("/verify-email", { state: { email: user.email } });
    }
  }, [token, user, navigate]);

  // Validate if input is email or username
  const validateInputType = (input: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(input);
  };

  const handleLoginChange = (value: string) => {
    setLoginInput(value);
    if (value) {
      setIsEmail(validateInputType(value));
    } else {
      setIsEmail(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginInput || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    // Validate format if it's an email
    if (validateInputType(loginInput) && !loginInput.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      const result = await dispatch(
        login({
          login: loginInput,
          password,
        }) as any
      );

      if (login.fulfilled.match(result)) {
        const response = result.payload;

        if (response.requires_verification) {
          toast.success("Verification code sent to your email!");
          navigate("/verify-email", {
            state: {
              email: validateInputType(loginInput)
                ? loginInput
                : response.user.email,
            },
          });
        } else {
          toast.success("Login successful!");
        }
      } else if (login.rejected.match(result)) {
        // Error is already handled by the interceptor, but we can add specific handling here if needed
        const errorMessage = result.payload as string;
        console.error("Login error:", errorMessage);
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
    }
  };

  const handleResendVerification = async () => {
    if (!loginInput) {
      toast.error("Please enter your email address or username first");
      return;
    }

    // If input is not email, we need to get the user's email first
    const isEmailInput = validateInputType(loginInput);
    let userEmail = loginInput;

    if (!isEmailInput) {
      try {
        // We need to fetch user to get their email
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user/find-by-username`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ username: loginInput }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          userEmail = data.email;
        } else {
          toast.error(
            "Could not find user. Please enter your email address instead."
          );
          return;
        }
      } catch (error) {
        toast.error("Failed to find user");
        return;
      }
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/resend-verification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: userEmail }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success("Verification code sent successfully! Check your email.");
        navigate("/verify-email", { state: { email: userEmail } });
      } else {
        toast.error(data.error || "Failed to resend verification code");
      }
    } catch (error) {
      toast.error("Failed to resend verification code");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-5">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="ObeliskRecovery" className="h-12 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome back
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Sign in to access your account
          </p>
        </div>

        {/* Login Form */}
        <div className={GlobalStyles.card}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email or Username */}
            <div>
              <label htmlFor="login" className={GlobalStyles.inputLabel}>
                Email or Username
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {isEmail === null ? (
                    <User className="h-5 w-5 text-gray-400" />
                  ) : isEmail ? (
                    <Mail className="h-5 w-5 text-gray-400" />
                  ) : (
                    <User className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <input
                  id="login"
                  name="login"
                  type="text"
                  autoComplete="username"
                  required
                  value={loginInput}
                  onChange={(e) => handleLoginChange(e.target.value)}
                  className={`${GlobalStyles.input} pl-10`}
                  placeholder="Email or username"
                  disabled={isLoading}
                />
                {/* Input type indicator */}
                {loginInput && isEmail !== null && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <span className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      {isEmail ? "Email" : "Username"}
                    </span>
                  </div>
                )}
              </div>
              {loginInput && isEmail !== null && (
                <p className="mt-1 text-xs text-gray-500">
                  You're logging in with {isEmail ? "email" : "username"}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className={GlobalStyles.inputLabel}>
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${GlobalStyles.input} pl-10 pr-10`}
                  placeholder="••••••••"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
              >
                Remember me
              </label>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} w-full`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div
                      className={`${GlobalStyles.spinner} w-5 h-5 mr-2`}
                    ></div>
                    Signing in...
                  </div>
                ) : (
                  "Sign In"
                )}
              </button>
            </div>
          </form>

          {/* Email Verification Notice */}
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                  Email verification required
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                  If you haven't verified your email yet, you'll be redirected
                  to the verification page after login.
                </p>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  className="mt-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                  disabled={isLoading}
                >
                  Resend verification code
                </button>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Don't have an account?
                </span>
              </div>
            </div>

            {/* Register Link */}
            <div className="mt-6">
              <Link
                to="/register"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full justify-center`}
              >
                Create new account
              </Link>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} mt-6`}>
          <Shield className="h-5 w-5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium mb-1">
              Your security is our priority
            </p>
            <p className="text-sm">
              All data is encrypted and securely stored. We use
              industry-standard security measures to protect your account.
            </p>
          </div>
        </div>

        {/* Additional Links */}
        <div className="mt-6 text-center space-y-2">
          <Link
            to="/terms"
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary transition-colors inline-block mx-2"
          >
            Terms of Service
          </Link>
          <Link
            to="/privacy"
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary transition-colors inline-block mx-2"
          >
            Privacy Policy
          </Link>
          <Link
            to="/about"
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary transition-colors inline-block mx-2"
          >
            About Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
